// The silent media element that carries Now Playing while a song plays through Web Audio (issue
// #3718): whether WebKit surfaces the lock-screen controls for Web Audio alone was never measured,
// and a looping silent element is known to carry Now Playing on iOS and the media notification on
// Android, so the Player plays one alongside a song and pauses it with the song. One for the page,
// made the first time a song plays.
//
// What goes wrong with the element is one console.log line prefixed `[audio-clock]`, which
// recordConsoleLog() keeps in localStorage on the device, beside the audio clock watchdog's lines
// (client/src/audio/page_audio_context.ts): on iOS the element is played by mediaplaybackd, a
// sibling of WebKit's audio session, and what it did while the page's clock stood still is part of
// the record of a silent play button (issue #4116). That is a refused play() and the events that
// say playback is in trouble (waiting, stalled, ended, error). The healthy play and pause log
// nothing here: the Player logs each as one line already, with whether this element is paused, and
// the routine events (play, playing, pause, and the suspend a looping data: URI fires once it has
// loaded) would push the lines that matter out of the record's 300.
//
// The element itself is behind createNowPlayingElementModule(), so the unit tests drive the real
// rules with a fake.

import { logAudioClock } from '../audio/audio_clock_log.ts';
import { createSilentAudioElement } from '../audio/audio_session.ts';
import { errorNameAndMessage } from '../util/describe_error.ts';

// The part of an audio element used here
export interface NowPlayingAudioElement {
  readonly paused: boolean;
  readonly currentTime: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: string, listener: () => void): void;
}

export interface NowPlayingElementModule {
  // Plays the element (made on first use) unless it is playing; a refusal leaves the lock screen
  // without controls, which is where it was before the element
  play(): void;
  // Pauses the element if it is playing
  pause(): void;
  // Whether the element is paused, true before it exists; for the diagnostics
  isPaused(): boolean;
}

// The element's events that say playback is in trouble, one line each
const loggedEvents = ['waiting', 'stalled', 'ended', 'error'];

function log(element: NowPlayingAudioElement, event: string) {
  logAudioClock(
    'now-playing element',
    event,
    undefined,
    `paused ${element.paused}`,
    `currentTime ${element.currentTime.toFixed(3)}`,
  );
}

// A module of its own around the elements `createAudioElement` makes, for tests; the app uses the
// page-wide nowPlayingElement below
export function createNowPlayingElementModule(
  createAudioElement: () => NowPlayingAudioElement,
): NowPlayingElementModule {
  let element: NowPlayingAudioElement | undefined;

  function ensureElement(): NowPlayingAudioElement {
    if (element) {
      return element;
    }
    const created = createAudioElement();
    for (const event of loggedEvents) {
      created.addEventListener(event, () => log(created, event));
    }
    element = created;
    return created;
  }

  function play() {
    const target = ensureElement();
    if (!target.paused) {
      return;
    }
    try {
      target.play().catch((error: unknown) => log(target, `play() rejected: ${errorNameAndMessage(error)}`));
    } catch (error) {
      // a browser that throws rather than rejects: the same refusal
      log(target, `play() threw: ${errorNameAndMessage(error)}`);
    }
  }

  function pause() {
    if (!element || element.paused) {
      return;
    }
    element.pause();
  }

  return { play, pause, isPaused: () => element?.paused ?? true };
}

// The page's one element, for the Player (see NowPlayingElementModule)
export const nowPlayingElement: NowPlayingElementModule = createNowPlayingElementModule(() =>
  createSilentAudioElement(),
);
